import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import { tileUrl, maskFill } from '../theme';

const THREAT_COLORS = {
  'High':      '#ff5d5d',
  'Medium':    '#ffb02e',
  'Low':       '#9aa0ab',
  'Own Brand': '#7db8ff',
  'Centre':    '#7db8ff',
};

export default function MapPanel({ onLocationSelect, location, radiusKm, competitors }) {
  const mapRef      = useRef(null);
  const mapObj      = useRef(null);
  const markerRef   = useRef(null);
  const circleRef   = useRef(null);
  const compMarkers = useRef([]);
  const tileRef     = useRef(null);
  const maskRef     = useRef(null);

  useEffect(() => {
    if (mapObj.current) return;

    mapObj.current = L.map(mapRef.current, {
      center: [-29.0, 25.0],
      zoom: 6,
      zoomControl: false,
    });

    tileRef.current = L.tileLayer(
      tileUrl(),
      {
        attribution: '© OpenStreetMap © CARTO',
        subdomains: 'abcd',
        maxZoom: 19,
      }
    ).addTo(mapObj.current);

    // Fade out countries where SiteIQ has no data coverage.
    const ACTIVE_COUNTRIES = ['South Africa'];
    fetch('https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson')
      .then(r => r.json())
      .then(geo => {
        const activeFeatures = geo.features.filter(f =>
          ACTIVE_COUNTRIES.includes(f.properties.ADMIN)
        );
        if (!activeFeatures.length) return;
        const worldBox = [[-90, -180], [-90, 180], [90, 180], [90, -180], [-90, -180]];
        const holes = [];
        activeFeatures.forEach(feature => {
          const coordSets = feature.geometry.type === 'MultiPolygon'
            ? feature.geometry.coordinates
            : [feature.geometry.coordinates];
          coordSets.forEach(poly =>
            poly.forEach(ring => holes.push(ring.map(c => [c[1], c[0]])))
          );
        });
        maskRef.current = L.polygon(
          [worldBox, ...holes],
          { ...maskFill(), interactive: false }
        ).addTo(mapObj.current);
      })
      .catch(() => {});

    mapObj.current.on('click', (e) => {
      onLocationSelect({ lat: e.latlng.lat, lon: e.latlng.lng });
    });

    // Swap basemap + coverage mask when the theme toggles.
    const onTheme = () => {
      if (tileRef.current) tileRef.current.setUrl(tileUrl());
      if (maskRef.current) maskRef.current.setStyle(maskFill());
    };
    window.addEventListener('themechange', onTheme);
    return () => window.removeEventListener('themechange', onTheme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pin + catchment circle follow `location` (clicks and programmatic moves)
  useEffect(() => {
    const map = mapObj.current;
    if (!map) return;
    if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; }
    if (circleRef.current) { circleRef.current.remove(); circleRef.current = null; }
    if (!location) return;

    const pinIcon = L.divIcon({
      html: '<div class="sq-pin"><span class="pulse"></span><span class="dot"></span></div>',
      iconSize: [14, 14],
      iconAnchor: [7, 7],
      className: '',
    });
    markerRef.current = L.marker([location.lat, location.lon], {
      icon: pinIcon,
      interactive: false,
    }).addTo(map);

    circleRef.current = L.circle([location.lat, location.lon], {
      radius: (radiusKm || 3) * 1000,
      color: '#ff6a3d',
      fillColor: '#ff6a3d',
      fillOpacity: 0.05,
      weight: 1.3,
      dashArray: '6 6',
      interactive: false,
    }).addTo(map);

    // Bring the pin into view when it was set programmatically (rail click,
    // payment return) without fighting the user while they pan around.
    const target = L.latLng(location.lat, location.lon);
    if (!map.getBounds().contains(target) || map.getZoom() < 10) {
      map.flyTo(target, Math.max(map.getZoom(), 12), { duration: 0.8 });
    }
  }, [location, radiusKm]);

  // Competitor / own-store dots
  useEffect(() => {
    const map = mapObj.current;
    if (!map) return;
    compMarkers.current.forEach(m => m.remove());
    compMarkers.current = [];
    (competitors || []).forEach(comp => {
      if (comp.lat == null || comp.lon == null) return;
      const color = THREAT_COLORS[comp.threat] || '#9aa0ab';
      const isCentre = comp.threat === 'Centre';
      const icon = L.divIcon({
        html: `<div style="
          width:${isCentre ? 12 : 10}px;height:${isCentre ? 12 : 10}px;
          background:${color};
          border:2px solid var(--s-solid);
          border-radius:${isCentre ? '3px' : '50%'};
          box-shadow:0 0 6px rgba(0,0,0,0.4);
        "></div>`,
        iconSize: [10, 10],
        iconAnchor: [5, 5],
        className: '',
      });
      const m = L.marker([comp.lat, comp.lon], { icon })
        .addTo(map)
        .bindPopup(`
          <div style="font-size:12px;min-width:130px">
            <strong>${comp.brand || comp.name || ''}</strong><br>
            ${comp.name || ''}<br>
            <span style="color:#8e919b">${comp.dist_km != null ? comp.dist_km + ' km · ' : ''}${comp.threat || ''}</span>
          </div>
        `);
      compMarkers.current.push(m);
    });
  }, [competitors]);

  return <div ref={mapRef} style={{ position: 'fixed', inset: 0, zIndex: 0 }} />;
}
